import PropTypes from 'prop-types';
import React, { useState } from 'react';
import styled from 'styled-components';
import { navigate } from 'gatsby';

import {
  AnimateIn,
  animation,
  colors,
  Container,
  ExternalLink,
  Heading,
  InternalLink,
  Label,
  media,
  Text,
  TitleSet
} from '../ui-components';

import CONTENT from '../static-content/contact.json';
import CelebrateIcon from '../assets/icons/celebrate80x80.svg';
import ContactUs from '../assets/icons/contact-us.svg';
import LinkedInIcon from '../assets/icons/linkedin-blue.svg';
import { switchContactForm } from '../utils/switch-contact-form';
import { primaryButtonStyles } from "../ui-components/components/component-styles/button";

const propTypes = {
  geoSpace: PropTypes.string,
  slug: PropTypes.string,
  formOnPage: PropTypes.bool,
  eventPage: PropTypes.bool,
  resultState: PropTypes.object,
  resultNavigation: PropTypes.string,
  customForm: PropTypes.object,
  openForm: PropTypes.bool,
  registrationForm: PropTypes.bool,
  submissionText: PropTypes.string
};

/**
 * Component that renders a contact form
 *
 * @component ContactForm
 *
 * @param {object} props
 *
 * @prop {string} geoSpace
 * @prop {string} slug
 * @prop {boolean} formOnPage
 * @prop {boolean} eventPage
 * @prop {object} resultState
 * @prop {string} resultNavigation
 * @prop {object} customForm
 * @prop {string} openForm - on event pages only
 * @prop {string} registrationForm - on registration pages only
 * @prop {string} submissionText
 */

const ContactForm = props => {
  const { geoSpace, slug, formOnPage = false, eventPage = false, resultState, resultNavigation, customForm, openForm = false, registrationForm = false, submissionText, isPreview, hiddenFieldText} = props;
  const [formComplete, setFormComplete] = useState(false);
  const formTitle = isPreview ? (customForm?.fields?.formTitle || CONTENT.CONTACT_FORM_HEADING) : (customForm?.formTitle || CONTENT.CONTACT_FORM_HEADING);
  const formSubmissionTitle = isPreview ? (customForm?.fields?.submissionTitle || CONTENT.REGISTRATION_FORM_HEADING) : (customForm?.submissionTitle || CONTENT.REGISTRATION_FORM_HEADING);
  const formBody = customForm?.formBody ? customForm?.formBody?.formBody : CONTENT.CONTACT_FORM_SUBHEADING;

  const renderTitleSection = () => {
    return (
      <>
        <ContactIcon src={ContactUs} alt={'Conversation Icon'} />
        <Label>{CONTENT.CONTACT_FORM_LABEL}</Label>
        <Heading level={2}>{formTitle}</Heading>
        <ContactSubheading>{formBody}</ContactSubheading>
      </>
    );
  };

  /**
   * Renders the contact form
   *
   * @function renderContactForm
   * @returns {JSX}
   */
  const renderContactForm = () => {
    return switchContactForm(customForm, geoSpace, setFormComplete, false, slug, submissionText, hiddenFieldText)
  };

  const renderFormComplete = () => {
    if (resultState && resultNavigation) {
      navigate(geoSpace ? `/${geoSpace}/${resultNavigation}` : `/${resultNavigation}`, {
        state: resultState
      });
    } else {
      return (
        <AnimateIn onScroll>
          <FormCompletedContainer>
            <FormCompleteIcon src={CelebrateIcon} alt="Celebration Icon" />
            <Heading level={2}>{customForm?.formTitle || CONTENT.CONTACT_FORM_SUBMITTED_HEADING}</Heading>
            {/* TODO: Design team still finalizing the copy here */}
            <Text>{CONTENT.CONTACT_FORM_SUBMITTED_SUBHEADING}</Text>
            <FollowUsLink linkstyle="secondary-link" target="_blank" href={'https://www.linkedin.com/company/credera/'}>
              <img
                src={LinkedInIcon}
                style={{ width: '28px', height: '28px', margin: '0 6px 0 0' }}
                alt="LinkedIn Logo Icon"
              />
              {'Follow us on LinkedIn'}
            </FollowUsLink>
          </FormCompletedContainer>
        </AnimateIn>
      );
    }
  };

  const renderRegistrationForm = () => {
    return (
      <RegistrationFormContainer>
        {formComplete ? <TitleSet title={formSubmissionTitle} alignLeft /> : <TitleSet title={formTitle} alignLeft />}
        {renderContactForm()}
      </RegistrationFormContainer>
    )
  };

  const renderOpenForm = () => {
    return (
      <ContactSection id={'Contact'}>
        <TitleSet title={formTitle} bodyText={formBody} />
        <ContactFormContainer>{renderContactForm()}</ContactFormContainer>
      </ContactSection>
    )
  };

  const renderFormOnPage = () => {
    return (
      <ContactPageContainer>
        {!formComplete ? (
          <>
            {eventPage ? <></> : <ContactPageTitleSection>{renderTitleSection()}</ContactPageTitleSection>}
            <ContactPageFormSection>{renderContactForm()}</ContactPageFormSection>
          </>
        ) : (
          <ContactPageFormComplete>{renderFormComplete()}</ContactPageFormComplete>
        )}
      </ContactPageContainer>
    );
  };

  const renderContactFormComponent = () => {
    return (
      <ContactComponentWrapper full formComplete={+formComplete} id={'Contact'}>
        {!formComplete ? (
          <ContactComponentContainer>
            <ContactComponentContent>
              <ContactComponentTitleSection>{renderTitleSection()}</ContactComponentTitleSection>
              <ContactComponentCareerCTA>
                <Text>{'Searching for a new career?'}</Text>
                <InternalLink to={'/careers'} withArrow>
                  {'View job openings'}
                </InternalLink>
              </ContactComponentCareerCTA>
            </ContactComponentContent>
            <ContactComponentFormSection>{renderContactForm()}</ContactComponentFormSection>
          </ContactComponentContainer>
        ) : (
          <ContactComponentFormComplete>{renderFormComplete()}</ContactComponentFormComplete>
        )}
      </ContactComponentWrapper>
    );
  };

  return (
    <>
      {registrationForm ? renderRegistrationForm() :
        openForm ? renderOpenForm() :
          formOnPage ? renderFormOnPage() :
            renderContactFormComponent()}
    </>
  );
};

const ContactPageContainer = styled(Container)`
  display: flex;
  flex-direction: column;
  gap: 60px;
  padding: 0;
  flex-wrap: wrap;
  align-content: center;
  min-height: 100%;
  justify-content: center;
  form {
    margin-bottom: 0;
  }

  @media screen and (max-width: ${media.largeBottom}) {
    gap: 40px;
  }
`;

const ContactPageTitleSection = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 620px;
  align-items: center;
  text-align: center;
  align-self: center;
`;

const ContactPageFormSection = styled.div`
  // TODO: Fix form styling in HubSpot to avoid this
  max-width: 840px;

  @media screen and (max-width: ${media.mediumTop}) {
    max-width: 100%;
  }
`;

const ContactPageFormComplete = styled.div``;

const ContactComponentWrapper = styled.div`
  padding: 0;
  max-width: none;

  @media screen and (width > ${media.largeBottom}) {
    display: flex;
    justify-content: center;
    padding: ${({ formComplete }) => (formComplete ? `100px 80px` : '0')};
    transition: ${animation.general.transition};
    background: ${({ formComplete }) =>
      formComplete
        ? `${colors.secondaryBackground}`
        : `linear-gradient(to right, ${colors.secondaryBackground} 50%, ${colors.sandBeige} 50%)`};
  }
`;

const ContactComponentContainer = styled(Container)`
  display: flex;
  flex: 1;
  gap: 24px;
  padding: 50px 80px 50px 80px;
  justify-content: space-between;

  @media screen and (max-width: ${media.largeTop}) {
    padding: 40px 20px 60px 20px;
  }

  @media screen and (max-width: ${media.largeBottom}) {
    flex-direction: column;
    gap: 40px;
    align-content: center;
    align-items: center;
  }
`;

const ContactComponentContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  max-width: 341px;
  padding: 7px 0 36px 0;

  @media screen and (max-width: ${media.largeBottom}) {
    max-width: none;
    padding: 0;
  }
`;

const ContactComponentTitleSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;

  @media screen and (max-width: ${media.largeBottom}) {
    align-items: center;
    text-align: center;
    max-width: 620px;
  }
`;

const ContactComponentCareerCTA = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  @media screen and (max-width: ${media.largeBottom}) {
    display: none;
  }
`;

const ContactComponentFormSection = styled.div`
  padding: 60px 80px 40px 80px;
  background-color: ${colors.gray5};
  box-shadow: 0 0 80px 0 rgba(58, 58, 58, 0.06);
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  justify-content: center;

  @media screen and (max-width: ${media.largeBottom}) {
    padding: 0;
    background-color: transparent;
    box-shadow: none;
  }

  width: 821px;

  @media screen and (max-width: ${media.mediumTop}) {
    width: 100%;

    form {
      margin-bottom: 0;
    }
  }
`;

const ContactComponentFormComplete = styled.div`
  @media screen and (min-width: ${media.largeBottom}) {
    max-width: 1280px;
    background-color: ${colors.gray5};
    box-shadow: 0 0 80px 0 rgba(58, 58, 58, 0.06);
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    padding: 180px 0;
  }
`;

const ContactIcon = styled.img`
  width: 80px;
  height: 80px;
  margin-bottom: 12px;

  @media screen and (max-width: ${media.largeBottom}) {
    margin-bottom: 8px;
  }
`;

const ContactSubheading = styled(Text)`
  margin-top: 12px;
`;

const FormCompletedContainer = styled(Container)`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  flex-direction: column;
  gap: 20px;
  padding: 0;
  max-width: 620px;

  @media screen and (max-width: ${media.largeBottom}) {
    padding: 100px 20px;
  }
`;

const FormCompleteIcon = styled.img`
  width: 80px;
  height: 80px;
  margin: 0;
`;

const FollowUsLink = styled(ExternalLink)`
  display: flex;
  align-items: center;
`;

const ContactSection = styled(Container)`
  background: ${colors.lightSky};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 80px;
  padding: 80px;

  @media screen and (max-width: ${media.mediumBottom}) {
    padding: 40px;
  }
`;

const ContactFormContainer = styled.div`
  width: 100%;
  max-width: 924px;
  padding: 80px;
  background: ${colors.sandBeige};

  .hs-button {
    ${primaryButtonStyles}
  }

  @media screen and (max-width: ${media.mediumBottom}) {
    padding: 20px;
  }
`;

const RegistrationFormContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  padding: 40px;
  gap: 40px;
  background: ${colors.gray5};
  box-shadow: 0 0 80px 0 rgba(58, 58, 58, 0.06);

  .hs-button {
    ${primaryButtonStyles}
  }

  @media screen and (max-width: ${media.mediumBottom}) {
    padding: 40px 20px 60px;
  }
`;

ContactForm.propTypes = propTypes;

export default ContactForm;
